import { BloomFilter } from 'bloomfilter';

/*
 * exitIntent
 *
 * Send an exit intent event to Google Tag Manager when the visitor
 * moves the mouse past the top of the window after viewing the
 * page for at least 10 seconds.
 *
 * The signal will not be sent if it has already been sent
 * for this page (or, via a cookie check, in the past 24 hours).
 */

/**
 * Store the set of excluded paths in this cookie, in the form of a
 * serialized Bloom filter.
 */
const CookieName = 'exitIntent';

/**
 * Wait this long before watching for exit intent.
 */
const ExitIntentSignalDelay = 10_000;

/**
 * Snooze exit intent popups for this many days.
 */
const ExitIntentSnoozeDays = 14;

let wasExitIntentSent = false;
let wasExitIntentSentAnywhere = window.getCookie(CookieName) !== '';
const bloomfilter = initBloomFilter();

export function initExitIntent(): void {
  if (didSendExitIntent()) return;

  setTimeout(
    () => document.addEventListener('mouseout', checkExitIntent),
    ExitIntentSignalDelay,
  );
}

/**
 * If the mouse moved out past the top of the window and we
 * haven't sent the exit intent event recently, then send
 * that event to GTM ( dataLayer.push(...) ) and update
 * the cookie to mark this document's path.
 */
function checkExitIntent(e: MouseEvent) {
  if (e.relatedTarget === null && e.clientY < 10) {
    document.removeEventListener('mouseout', checkExitIntent);
    let didEvent = false;

    if (!wasExitIntentSentAnywhere) {
      wasExitIntentSentAnywhere = true;
      window.dataLayer.push({ event: 'exit_intent' });
      didEvent = true;
    }

    if (!didSendExitIntent()) {
      window.dataLayer.push({ event: 'page_exit_intent' });
      wasExitIntentSent = true;
      didEvent = true;
    }

    if (didEvent) {
      bloomfilter.add(exitIntentKey());
      setCookie(
        CookieName,
        encodeURIComponent(exportBloomFilter(bloomfilter)),
        ExitIntentSnoozeDays,
      );
    }
  }
}

/**
 * Did we send the exit intent event on this pageview, or
 * within the past day on this page?
 */
function didSendExitIntent(): boolean {
  return wasExitIntentSent || bloomfilter.test(exitIntentKey());
}

/**
 * Use this key to decide whether the popup has been shown before.
 *
 * By default, uses the pathname for the page, but can be overridden
 * by setting window.MC.popupGroupKey to some nonempty string.
 */
function exitIntentKey(): string {
  return String(window.MC?.popupGroupKey) || window.location.pathname;
}

/**
 * Initialize the Bloom filter of pathnames from the base64-encoded cookie.
 *
 * If the cookie is not present (or invalid), initialize an empty Bloom filter.
 */
function initBloomFilter(): BloomFilter {
  const cookie = window.getCookie(CookieName);

  return (cookie && importBloomFilter(cookie)) || new BloomFilter(32 * 256, 16);
}

function importBloomFilter(s: string): BloomFilter | null {
  try {
    const array = new Int32Array(base64ToArrayBuffer(decodeURIComponent(s)));
    return new BloomFilter(array, 16);
  } catch {
    return null;
  }
}

function exportBloomFilter(bf: BloomFilter): string {
  return arrayBufferToBase64(bf.buckets.buffer);
}

function setCookie(key: string, value: string, days: number) {
  const today = new Date();
  const expirationDate = new Date();
  expirationDate.setTime(today.getTime() + days * 86_400_000);
  document.cookie = `${key}=${value};samesite=lax;path=/;expires=${expirationDate.toUTCString()}`;
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;

  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return window.btoa(binary);
}

function base64ToArrayBuffer(base64: string) {
  const binary_string = window.atob(base64);
  const len = binary_string.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i);
  }
  return bytes.buffer;
}
